/**
 * Interior EDITOR logic (ADR 0056, plan §5/4c — the Designer Portal repo's
 * docs/PRICING_AND_INTERIORS_PLAN.md).
 *
 * The testable half of the per-section interior editor. app.js owns the DOM
 * (overlay, rows, buttons); everything with semantics lives here, DOM-free:
 *
 *   - the DRAFT: a working copy of one section's composition + doors. Every
 *     edit in the editor mutates the draft only; the model changes NOTHING
 *     until applyInteriorDraft() — that is what makes Cancel a plain discard
 *     and Apply a single commit (staging semantics, tested at this level).
 *   - draft operations: reorder (up/down — the repo's reorder precedent, no
 *     drag), remove, add-by-kind, per-drawer height edits, bank presets,
 *     shoe style/count, shelf fixed/pin, doors coverage.
 *   - the DOOR DEPTH rule: doors need `door_min_depth_units` of section depth
 *     (business rule, ADR 0056 — owner-tunable data, not code). Blocked doors
 *     are a WARNING + a refused doors-commit; interior edits are never
 *     depth-blocked.
 *   - the LIVE PREVIEW feed: previewElevation() builds a synthetic one-panel
 *     wall plus substituted option feeds so the REAL createElevation renders
 *     exactly this section's stack — the measurement sheet's proven pattern
 *     (ADR 0045/0055) of substituting feeds rather than re-implementing a
 *     renderer. The staged draft rides in through the `interiorFor` feed, so
 *     the preview is the same solver + the same marks the wall elevation will
 *     draw after Apply, never a re-implementation.
 */
import {
  DOOR_COVERAGES,
  SHOE_SHELF_STYLES,
  drawerBankBandHeightUnits,
  drawerStackHeightUnits,
  ensureInteriorStructure,
  coalesceOpenSpaces,
  interiorBand,
  interiorOpenings,
  interiorSpaces,
  interiorStructureHealing,
  isSpaceKind,
  normalizeDoors,
  normalizeInteriorElement,
  roundDrawerFaceUnits,
  shelfThicknessUnits,
  solveInterior,
  spaceSplitDefaultUnits,
} from '../geometry/interior.js';
import { interiorSolveOpts } from '../config/business-rules.js';

const EPS = 0.01;

/**
 * Whether the STAGED composition hangs clothes — the draft-side twin of the
 * model's sectionHasHang (ADR 0057): a rod ANYWHERE in the section, not just
 * behind the door band (owner, 2026-08-09). It reads the DRAFT so adding a rod
 * raises the door-depth minimum in the same render, before anything is
 * committed.
 */
export function draftHasHang(draft) {
  return (draft?.elements ?? []).some((el) => el?.kind === 'rod');
}

/**
 * A fresh working copy of a section's composition. Everything is deep-copied
 * through the normalizers, so no draft edit can reach the committed state.
 *
 * @param {Array<object>|null} interior what model.sectionInterior(id) returned
 *   (already the EFFECTIVE composition — stored, or derived for a legacy
 *   section, so editing a legacy section starts from what the elevation shows)
 * @param {object|null} doors the section's committed doors
 * @returns {{elements: Array<object>, doors: object|null}}
 */
export function createInteriorDraft(interior, doors, requiredHolesOnly = false) {
  // HEAL ON OPEN (ADR 0065 part 1). A stored composition older than an
  // invariant is missing that invariant's boards, and nothing rewrites stored
  // sketches on load. Opening the section for editing is where they arrive:
  // into the DRAFT, so they are visible in the list and in the live preview
  // before anything is committed, Cancel leaves the stored section untouched,
  // and Apply is what persists them. `healed` is the report the editor turns
  // into an inline note — the owner must be TOLD a board appeared, never have
  // one arrive silently.
  // NOT coalesced here, deliberately. Two abutting empty spaces are a LEGAL
  // composition that the solver divides evenly between them — see
  // interior-spaces.test.js, "two flexible spaces and NO pin still divide the
  // band evenly", which is the layout the owner validated as "genuinely even".
  // Opening a section must not rewrite a shape the model considers valid. What
  // gets fixed is the EDIT that STRANDS a designer in it (see restructure).
  const { elements, added } = interiorStructureHealing(Array.isArray(interior) ? interior : []);
  return {
    elements,
    healed: added,
    doors: normalizeDoors(doors),
    // The per-section drilling mode rides in the DRAFT like everything else,
    // so toggling it re-solves the live preview before anything is committed
    // (ADR 0061 item B — the same staging contract the doors already have).
    requiredHolesOnly: requiredHolesOnly === true,
  };
}

/**
 * How a healed board is NAMED to the designer (ADR 0065 part 1). Two phrasings
 * per role, because "Added 2 fixed board above the drawer bank" is the kind of
 * sentence that makes a designer distrust the tool that wrote it.
 *
 * The role comes from structuralShelfRoles(), i.e. from ADJACENCY — the same
 * source the element list's own " · over the drawers" labels read — so the note
 * and the label can never name the board differently.
 */
export const HEALED_BOARD_PHRASES = Object.freeze({
  rodSupport: ['the fixed board above the rod', 'fixed boards above rods'],
  drawerBankAbove: ['the fixed board above the drawer bank', 'fixed boards above drawer banks'],
  drawerBankBelow: ['the fixed board below the drawer bank', 'fixed boards below drawer banks'],
  // A SPACE, not a board (ADR 0067), so it gets its own sentence rather than
  // being called "required structure": nothing was built, a region the drawing
  // already showed was given the row it never had.
  bandSpace: ['the empty space the boards left unlisted', 'empty spaces the boards left unlisted',
    'it was already in the drawing'],
});

/** The fallback phrasing for a healed board whose role adjacency no longer
 *  reports (it cannot happen today — every insert is beside the thing that
 *  needed it — but a null role must still produce a sentence, not "undefined"). */
const HEALED_BOARD_FALLBACK = ['a required fixed board', 'required fixed boards'];

/**
 * The inline notes the editor shows for what OPENING this section healed
 * (ADR 0065 part 1) — one sentence per KIND of board added, counted.
 *
 * An empty array when the stored composition already conformed, which is the
 * common case and must be silent: a note that appears every time says nothing.
 *
 * @returns {Array<string>}
 */
export function draftHealingNotes(draft) {
  const added = Array.isArray(draft?.healed) ? draft.healed : [];
  const byRole = new Map();
  for (const { role } of added) {
    const key = HEALED_BOARD_PHRASES[role] ? role : '_';
    byRole.set(key, (byRole.get(key) ?? 0) + 1);
  }
  return [...byRole.entries()].map(([role, n]) => {
    const [one, many, tail] = HEALED_BOARD_PHRASES[role] ?? HEALED_BOARD_FALLBACK;
    const why = tail ?? 'required structure';
    return n === 1
      ? `Added ${one} — ${why}.`
      : `Added ${n} ${many} — ${why}.`;
  });
}

/** Stages "required holes only" for the section (ADR 0061 item B). */
export function draftSetRequiredHolesOnly(draft, on) {
  draft.requiredHolesOnly = on === true;
  return true;
}

/**
 * Tags a shelf — or a whole shelf GROUP — as DOUBLE-THICK (ADR 0061 item C):
 * ONE board of 2× the standard thickness, which is what the owner asked to
 * replace "putting shelves vertically directly next to each other".
 */
export function draftSetShelfDoubleThick(draft, elIndex, doubleThick) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shelf' && el?.kind !== 'shelfGroup') return false;
  el.doubleThick = doubleThick === true;
  return true;
}

/**
 * Moves the element at `index` up (dir -1) or down (dir +1) the top→bottom list.
 *
 * Reordering deliberately does NOT re-run the structural helpers (ADR 0062):
 * moving a bank three rows down is three presses of ▼, and healing at each one
 * would plant a pair of shelves at every intermediate position the designer was
 * only passing through. A reorder is a statement about the elements that exist;
 * the next add/remove — and the Apply commit — restore the invariants.
 */
export function draftMove(draft, index, dir) {
  const to = index + (dir < 0 ? -1 : 1);
  if (index < 0 || index >= draft.elements.length || to < 0 || to >= draft.elements.length) return false;
  const [el] = draft.elements.splice(index, 1);
  draft.elements.splice(to, 0, el);
  return true;
}

/**
 * Removes the element at `index` — and immediately RE-ESTABLISHES the
 * structural invariants (ADR 0062 item 6): deleting a rod's support shelf, or
 * a drawer bank's sandwich shelf, puts it straight back. The designer sees the
 * board reappear, which says "this one is structure" better than a refusal
 * dialog would, and it is the same answer for both invariants.
 *
 * Deleting the ROD (or the BANK) itself of course removes it — the shelf then
 * has no structural job and simply stays as an ordinary fixed shelf, which is
 * a real thing to have and never a surprise deletion of the designer's board.
 */
/**
 * Re-establish the structural invariants after an edit that changed the SHAPE
 * of the list, and collapse any run of empty spaces the edit left behind.
 *
 * Both halves, in this order, every time. Inserting a support board can only
 * SEPARATE two open spaces, never join them, so coalescing last is correct;
 * doing it first would let a board land between spaces that then stayed split.
 *
 * Three edits can leave two empty spaces abutting — removing the board between
 * them, filling a treated space with Empty beside one, and (defensively)
 * merging. Routing all three through one helper is what stops a fourth from
 * being added without it.
 */
function restructure(elements) {
  return coalesceOpenSpaces(ensureInteriorStructure(elements));
}

export function draftRemove(draft, index) {
  if (index < 0 || index >= draft.elements.length) return false;
  draft.elements.splice(index, 1);
  draft.elements = restructure(draft.elements);
  return true;
}

/**
 * Appends a new element of `kind` (bottom of the list — where the designer is
 * looking after scrolling through the composition). Seeds:
 *   - drawerBank: ONE drawer at the default face height (the bank presets are
 *     the one-tap path for real banks — draftAddBank);
 *   - shoeShelves: FSS at the shoes type's default shelf count (data-driven
 *     from the presets; 7 — the compiled SS template — when no shoes type
 *     states one);
 *   - shelf: adjustable (fixed=false), unpinned;
 *   - shelfGroup: `count` evenly spaced shelves in one element (ADR 0060 item
 *     9 — the designer states a NUMBER instead of tapping add five times);
 *   - rod: no category (a designer's ad-hoc rod; typed sections get theirs
 *     from the template) — and its structural SUPPORT SHELF, inserted by the
 *     same ensureRodSupportShelves() the derivation uses, so a rod added here
 *     and a rod derived from a type get identical compositions (ADR 0060 item
 *     4). Adding a rod also flips the section to HANGING for the door-depth
 *     rule, in this same edit.
 * @returns {number} the new element's index, or -1
 */
export function draftAdd(draft, kind, rules = {}, seedOpts = {}) {
  const face = rules.defaultDrawerHeightUnits > 0 ? rules.defaultDrawerHeightUnits : 6.25;
  const shoesPreset = (Array.isArray(rules.sectionTypePresets) ? rules.sectionTypePresets : [])
    .find((p) => Array.isArray(p.flags) && p.flags.includes('shoes'));
  const shoeCount = Number.isInteger(shoesPreset?.defaultShelves) && shoesPreset.defaultShelves > 0
    ? shoesPreset.defaultShelves : 7;
  const groupCount = Number.isInteger(seedOpts.count) && seedOpts.count > 0 ? seedOpts.count : 5;
  const seeds = {
    rod: { kind: 'rod', hangCategory: null },
    shelf: { kind: 'shelf', fixed: false },
    shelfGroup: { kind: 'shelfGroup', count: groupCount, fixed: false },
    drawerBank: { kind: 'drawerBank', drawers: [{ heightUnits: face }] },
    shoeShelves: { kind: 'shoeShelves', style: 'FSS', count: shoeCount },
    openSpace: { kind: 'openSpace' },
  };
  const el = normalizeInteriorElement(seeds[kind]);
  if (!el) return -1;
  draft.elements.push(el);
  // The structural invariants run over the WHOLE draft after every add — one
  // idempotent helper (ADR 0060 item 4 + ADR 0062), so a rod added here and a
  // rod derived from a type get identical compositions, and a bank added here
  // arrives sandwiched between its fixed shelves exactly as a preset's does.
  draft.elements = ensureInteriorStructure(draft.elements);
  // The added element is still the LAST one whatever the helper inserted: the
  // helper only ever inserts ABOVE an appended element (a bank's below-shelf
  // needs something under the bank, and nothing is under the end of the list).
  return draft.elements.length - 1;
}

/** Sets a shelf GROUP's shelf count (whole, >= 0) — the count-based insert
 *  the owner asked for; the preview re-solves the pitch on the next render. */
export function draftSetShelfGroupCount(draft, elIndex, count) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shelfGroup' || !Number.isInteger(count) || count < 0) return false;
  el.count = count;
  return true;
}

/** Toggles a shelf GROUP between adjustable and fixed shelves. */
export function draftSetShelfGroupFixed(draft, elIndex, fixed) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shelfGroup') return false;
  el.fixed = fixed === true;
  return true;
}

/**
 * Appends a whole drawer BANK from a `drawer_bank_presets` entry — the
 * one-tap inserts the plan calls for (the three real CV banks).
 */
export function draftAddBank(draft, heights) {
  const faces = (Array.isArray(heights) ? heights : []).filter((h) => Number.isFinite(h) && h > 0);
  if (!faces.length) return -1;
  const el = normalizeInteriorElement({ kind: 'drawerBank', drawers: faces.map((h) => ({ heightUnits: h })) });
  draft.elements.push(el);
  // A one-tap bank arrives with its fixed shelves, the same as a composed or a
  // derived one (ADR 0062) — the add-bank path is the reason the helper is
  // shared rather than living inside the derivation. The bank is still last
  // (the helper inserts only above an appended element).
  draft.elements = ensureInteriorStructure(draft.elements);
  return draft.elements.length - 1;
}

/**
 * Sets one drawer's face height inside a bank. Rejects junk, never coerces.
 *
 * The chosen height ROUNDS to a quarter inch (owner, 2026-08-09) — with one
 * exception: re-selecting the drawer's OWN current height is a no-op, so a
 * legacy CV face (7.42, 8.66, 8.68, 12.44) offered back in the select is not
 * silently rewritten by the act of confirming it. Choosing a different height
 * always lands on the quarter.
 *
 * The bank is RE-SORTED after the edit (tallest at the bottom, ADR 0060 item
 * 6), which is why `drawerIndex` addresses the list BEFORE the edit and the
 * caller must re-render rather than reuse it.
 */
export function draftSetDrawerHeight(draft, elIndex, drawerIndex, units) {
  const bank = draft.elements[elIndex];
  if (bank?.kind !== 'drawerBank' || !bank.drawers[drawerIndex]) return false;
  // A FIXED face (D113: hamper hardware dictates its height) refuses the
  // edit outright — refused, not clamped, the house rule — while every
  // other face in the same bank keeps editing freely.
  if (bank.drawers[drawerIndex].fixed === true) return false;
  if (!Number.isFinite(units) || !(units > 0)) return false;
  const current = bank.drawers[drawerIndex].heightUnits;
  const height = Math.abs(units - current) < 1e-9 ? current : roundDrawerFaceUnits(units);
  if (!(height > 0)) return false;
  const drawers = bank.drawers.map((d, i) => (i === drawerIndex ? { heightUnits: height } : d));
  draft.elements[elIndex] = normalizeInteriorElement({ ...bank, drawers });
  return true;
}

/** Adds one drawer to a bank, repeating its last face (else the default). The
 *  bank re-sorts, so the new face lands wherever its height belongs. */
export function draftAddDrawer(draft, elIndex, rules = {}) {
  const bank = draft.elements[elIndex];
  if (bank?.kind !== 'drawerBank') return false;
  const last = bank.drawers[bank.drawers.length - 1];
  const face = last?.heightUnits
    ?? (rules.defaultDrawerHeightUnits > 0 ? rules.defaultDrawerHeightUnits : 6.25);
  draft.elements[elIndex] = normalizeInteriorElement({
    ...bank, drawers: [...bank.drawers, { heightUnits: face }],
  });
  return true;
}

/**
 * The standard face heights offered for ONE drawer (ADR 0060 item 5): the
 * `drawer_face_heights` rule, plus this drawer's CURRENT height when it is not
 * on the list — a CV-derived bank's 7.42 must stay pickable and labelled
 * as-is, never silently snapped. Sorted, so the select reads as one list.
 *
 * @returns {Array<{units:number, standard:boolean}>}
 */
export function drawerHeightOptions(currentUnits, rules = {}) {
  const list = (Array.isArray(rules.drawerFaceHeights) ? rules.drawerFaceHeights : [])
    .filter((h) => Number.isFinite(h) && h > 0);
  const out = list.map((units) => ({ units, standard: true }));
  if (Number.isFinite(currentUnits) && currentUnits > 0
    && !list.some((h) => Math.abs(h - currentUnits) < 1e-9)) {
    out.push({ units: currentUnits, standard: false });
  }
  return out.sort((a, b) => a.units - b.units);
}

/**
 * A drawer bank's real stack height and whether that HEIGHT alone trips the
 * SSH drawer tool's maximum (ADR 0060 item 10).
 *
 * NOTE (ADR 0062): the app's warning is the SOLVER's `stackOverMax`, which
 * judges the maximum where the owner measures it — the TOP OF THE TOP DRAWER
 * FACE, off the floor. That needs a solved elevation, which this bare-faces
 * helper does not have. The two agree for a bank standing at the floor and
 * diverge for one composed higher up, where the solver is the authority.
 *
 * @returns {{heightUnits:number, overMax:boolean, maxUnits:number}}
 */
export function drawerStackState(drawers, rules = {}) {
  const maxUnits = rules.drawerStackMaxHeightUnits > 0 ? rules.drawerStackMaxHeightUnits : 48;
  const heightUnits = drawerStackHeightUnits(drawers, interiorSolveOpts(rules));
  return { heightUnits, overMax: heightUnits > maxUnits + 1e-9, maxUnits };
}

/** Removes one drawer from a bank. The last drawer may go — an empty bank is
 *  legal in the model (it solves to zero height) and reads as "remove me". */
export function draftRemoveDrawer(draft, elIndex, drawerIndex) {
  const bank = draft.elements[elIndex];
  if (bank?.kind !== 'drawerBank' || !bank.drawers[drawerIndex]) return false;
  bank.drawers.splice(drawerIndex, 1);
  return true;
}

/** Sets a shoeShelves element's style (FSS flat / SSS slanted). */
export function draftSetShoeStyle(draft, elIndex, style) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shoeShelves' || !SHOE_SHELF_STYLES.includes(style)) return false;
  el.style = style;
  return true;
}

/** Sets a shoeShelves element's shelf count (whole, >= 0). */
export function draftSetShoeCount(draft, elIndex, count) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shoeShelves' || !Number.isInteger(count) || count < 0) return false;
  el.count = count;
  return true;
}

/** Toggles a shelf element between adjustable and fixed. */
export function draftSetShelfFixed(draft, elIndex, fixed) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shelf') return false;
  el.fixed = fixed === true;
  return true;
}

/**
 * The DISCRETE boards bounding one space — the shelves a designer would call
 * "the one above this opening" and "the one below it".
 *
 * ## A board belongs to BOTH the spaces it separates
 *
 * The list is ordered top→bottom, so the element BEFORE a space sits ABOVE it
 * and the one AFTER sits BELOW. A board between two spaces therefore appears on
 * two rows, and that is deliberate (owner ruling, 2026-08-30): a designer
 * thinking "the shelf under this space" and one thinking "the shelf above that
 * space" are both right, and a rule about which row OWNS the board would be one
 * more thing to learn and to get wrong.
 *
 * Two VIEWS of one control, never two controls: both rows report the same
 * `locked` value read from the same element, and either toggles that element.
 * Nothing here copies state.
 *
 * Only DISCRETE shelves qualify. A shelfGroup is one element with a count and
 * has no per-board identity to lock or to set fixed, and a drawer bank or rod is not a board —
 * when a space abuts one of those, that side simply has no control rather than
 * a disabled one for something that was never lockable.
 *
 * @param {{elements: Array<object>}} draft
 * @param {number} elementIndex the space's own index
 * @returns {{above: {index:number, locked:boolean, fixed:boolean}|null,
 *            below: {index:number, locked:boolean, fixed:boolean}|null}}
 */
export function spaceBoundaryShelves(draft, elementIndex) {
  const els = Array.isArray(draft?.elements) ? draft.elements : [];
  const at = (i) => {
    const el = els[i];
    if (el?.kind !== 'shelf') return null;
    // Every per-board property the space rows offer comes from HERE, read off
    // the element itself on every render. Two spaces showing the same board
    // therefore cannot drift, because neither holds a copy of anything.
    return { index: i, locked: el.locked === true, fixed: el.fixed === true };
  };
  return { above: at(elementIndex - 1), below: at(elementIndex + 1) };
}

/**
 * Locks or unlocks ONE shelf (owner, 2026-08-30: "a way to lock a shelf so it
 * cannot be inadvertently moved").
 *
 * The fixture lock's meaning exactly, and deliberately so — app.js states it on
 * the obstruction control: "a locked fixture cannot be dragged (accidentally
 * nudged), though its measurements are still typeable". A lock stops the
 * GESTURE, not the number. A drag is what happens by accident; typing a height
 * into "Pin at" is not, and leaving that route open keeps the lock from
 * becoming a trap the designer has to undo before making a deliberate edit.
 *
 * NOT the same axis as `fixed`, and worth stating because the two get
 * conflated: `fixed` is how the board is ATTACHED — rafix bolts through the
 * panel versus resting on pins — and both kinds are movable. `locked` is
 * whether the designer may move it. Either can be set without the other.
 */
export function draftSetShelfLocked(draft, elIndex, locked) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shelf') return false;
  el.locked = locked === true;
  return true;
}

/**
 * Pins a shelf's TOP surface at an elevation off the floor (the figure a
 * designer states with a tape — ADR 0054), or clears the pin with null.
 * An invalid pin is REJECTED here; one that is valid data but cannot hold in
 * the band is the solver's `pinIgnored` flag, surfaced as a warning.
 */
export function draftSetShelfPin(draft, elIndex, atUnits) {
  const el = draft.elements[elIndex];
  if (el?.kind !== 'shelf') return false;
  if (atUnits === null) { delete el.atUnits; return true; }
  if (!Number.isFinite(atUnits) || !(atUnits > 0)) return false;
  el.atUnits = atUnits;
  return true;
}

/**
 * Pins every unpinned discrete shelf at the height it is ALREADY solved to.
 *
 * ## Why a drag needs this, and live verification is what proved it
 *
 * A shelf without a pin solves IN FLOW: the solver distributes it through
 * whatever clear space its segment has. Pinning one shelf therefore re-cuts the
 * segments around it and the flowing shelves redistribute — so dragging one
 * board visibly moved all five in the editor, which is precisely what ruling 3
 * (owner, 2026-08-29) forbids: *"dragging one shelf moves that shelf"*. No unit
 * test caught it, because a test asserts on the board it dragged.
 *
 * Materializing the solved positions first anchors the others, so the drag
 * moves exactly one board. This is not a new idea invented for the drag —
 * `draftInsertShelvesIntoOpening` already does the same thing for the same
 * stated reason (see its note: the pins are what "a later drag phase edits").
 *
 * ## What it costs, stated plainly
 *
 * Shelves that were flowing become pinned, which is a real change to the
 * composition and not merely a rendering detail: they stop redistributing when
 * the band changes. It is reversible through a control that already exists —
 * clearing the "Pin at" field returns a shelf to flow — and it is the smaller
 * of the two surprises, because the alternative is boards moving that the
 * designer never touched.
 *
 * Idempotent: a shelf that already carries a pin keeps the designer's number
 * rather than having it rewritten with the solver's.
 *
 * @param {object} draft the staged composition
 * @param {object} unit the placed unit it solves in (previewElevation's editedUnit)
 * @param {object} [rules] resolved business rules
 * @returns {boolean} true when at least one pin was materialized
 */
export function draftPinShelvesAtSolved(draft, unit, rules = {}) {
  const feed = draftFeedForUnit(draft, unit, rules);
  const solved = feed?.solved?.elements;
  if (!Array.isArray(solved)) return false;
  let changed = false;
  draft.elements.forEach((el, index) => {
    if (el?.kind !== 'shelf') return;         // a group is one element; not a pin target
    if (Number.isFinite(el.atUnits) && el.atUnits > 0) return; // the designer's own number wins
    const at = solved[index]?.toUnits;        // the shelf's TOP, which is what a pin states
    if (!Number.isFinite(at) || !(at > 0)) return;
    el.atUnits = at;
    changed = true;
  });
  return changed;
}

/**
 * Stages the section's doors: a coverage from DOOR_COVERAGES, or null (none).
 * A coverage change KEEPS any height already stated for lower/upper, and
 * `full` drops it (its extent is derived from the drawers — ADR 0060 item 2).
 */
export function draftSetDoors(draft, coverage) {
  if (coverage === null) { draft.doors = null; return true; }
  if (!DOOR_COVERAGES.includes(coverage)) return false;
  draft.doors = normalizeDoors({
    coverage,
    heightUnits: draft.doors?.heightUnits ?? null,
    splitAtUnits: draft.doors?.splitAtUnits ?? null,
    handleType: draft.doors?.handleType ?? null,
  });
  return true;
}

/** Stages the first/last covered SPACE identities. Order is deliberately not
 * stored as geometry: the solved composition establishes which is above. */
export function draftSetDoorExtent(draft, fromElementId, toElementId) {
  if (fromElementId === null && toElementId === null) { draft.doors = null; return true; }
  const ids = new Set((draft?.elements ?? []).filter((el) => isSpaceKind(el?.kind)).map((el) => el.id));
  if (!ids.has(fromElementId) || !ids.has(toElementId)) return false;
  draft.doors = normalizeDoors({
    fromElementId,
    toElementId,
    styleKey: draft.doors?.styleKey ?? null,
    handleType: draft.doors?.handleType ?? null,
  });
  return true;
}

/** Stages an exact style key; the resolved rule decides whether it can price. */
export function draftSetDoorStyle(draft, styleKey) {
  if (!draft.doors?.fromElementId || !draft.doors?.toElementId) return false;
  draft.doors = normalizeDoors({ ...draft.doors, styleKey: styleKey || null });
  return true;
}

/** Stages the handle used by this section's door leaves. */
export function draftSetDoorHandleType(draft, type) {
  if (!draft.doors) return false;
  draft.doors = normalizeDoors({ ...draft.doors, handleType: type || null });
  return true;
}

/**
 * Stages the elevation an upper and a lower door MEET at (`split` coverage,
 * ADR 0060 item 3). `null` restores the midpoint of the door extent — which is
 * also what a freshly chosen split starts at, so "unstated" and "default" are
 * the same state and there is no moment where the two are out of step.
 *
 * ONE number, deliberately: it is what the owner's drag-the-edge gesture will
 * edit, and it keeps the two bands complementary — no gap, no overlap.
 */
export function draftSetDoorSplit(draft, units) {
  if (draft.doors?.coverage !== 'split') return false;
  if (units === null) { draft.doors = normalizeDoors({ ...draft.doors, splitAtUnits: null }); return true; }
  if (!Number.isFinite(units) || !(units > 0)) return false;
  draft.doors = normalizeDoors({ ...draft.doors, splitAtUnits: units });
  return true;
}

/**
 * Stages the HEIGHT of a `lower`/`upper` door band (ADR 0060 item 3) — the
 * band the designer sets with the numeric pad; the rest of the opening is the
 * remainder. `null` restores the legacy half. Refused when there are no doors
 * or the coverage is `full`, which has no height to set.
 *
 * A height taller than the band is NOT rejected here — the solver clamps it
 * and flags `clamped`, so the warning explains rather than the field silently
 * refusing a figure the designer measured.
 */
export function draftSetDoorHeight(draft, units) {
  if (!draft.doors || draft.doors.coverage === 'full') return false;
  if (units === null) { draft.doors = normalizeDoors({ ...draft.doors, heightUnits: null }); return true; }
  if (!Number.isFinite(units) || !(units > 0)) return false;
  draft.doors = normalizeDoors({ ...draft.doors, heightUnits: units });
  return true;
}

/**
 * What the element list appends to a shelf that is doing a STRUCTURAL job
 * (ADR 0062 item 6) — the roles structuralShelfRoles() reports. A designer
 * seeing "Shelf — fixed · under the drawers" knows why the board is there and
 * that the app maintains it; deleting it puts it straight back.
 */
export const STRUCTURAL_ROLE_LABELS = Object.freeze({
  rodSupport: ' · rod support',
  drawerBankAbove: ' · over the drawers',
  drawerBankBelow: ' · under the drawers',
});

/** A compact human label for an element row ("Drawer bank · 4", "Rod (short)"). */
export function elementLabel(el) {
  switch (el?.kind) {
    case 'rod': return el.hangCategory ? `Rod (${el.hangCategory})` : 'Rod';
    case 'shelf': return `${el.doubleThick ? 'Shelf (double) — ' : 'Shelf — '}${el.fixed ? 'fixed' : 'adjustable'}`;
    case 'shelfGroup': return `Shelves · ${el.count}${el.fixed ? ' fixed' : ''}${el.doubleThick ? ' double' : ''}`;
    case 'drawerBank': return `Drawer bank · ${el.drawers?.length ?? 0}`;
    case 'shoeShelves': return `Shoe shelves · ${el.style} × ${el.count}`;
    case 'openSpace': return 'Open space';
    default: return el?.kind ?? '?';
  }
}

/**
 * The DOOR DEPTH rule (ADR 0056, REVISED by ADR 0060 items 1 + the owner's
 * 2026-08-09 correction).
 *
 * Two minimums, both business rules:
 *   - a HANGING section — one whose composition holds a rod ANYWHERE, not just
 *     behind the door band — needs `door_min_depth_hanging_units` (24"),
 *     because the clothes on the rod need the depth, not the door;
 *   - anything else needs only `door_min_depth_units` (4"), a sanity floor:
 *     "doors can go on any section deeper than 4"". A 12" shelving unit takes
 *     doors; the same unit takes a rod and immediately cannot.
 *
 * Depth is the RESOLVED depth of the placed unit (own over a base, inherited
 * above a non-base — what stacksForWall() already solves), so the rule judges
 * the carcass that would actually carry the door.
 *
 * @param {number} depthUnits resolved depth of the placed unit
 * @param {object} [rules] resolved business rules
 * @param {{hanging?: boolean}} [state] whether the composition holds a rod —
 *   pass draftHasHang(draft) in the editor, model.sectionHasHang() elsewhere
 * @returns {{blocked: boolean, minUnits: number, hanging: boolean}}
 */
export function doorDepthState(depthUnits, rules = {}, { hanging = false } = {}) {
  const minUnits = hanging
    ? (rules.doorMinDepthHangingUnits > 0 ? rules.doorMinDepthHangingUnits : 24)
    : (rules.doorMinDepthUnits > 0 ? rules.doorMinDepthUnits : 4);
  return { blocked: !(depthUnits > 0) || depthUnits + EPS < minUnits, minUnits, hanging: hanging === true };
}

/**
 * The staged draft solved in a PLACED unit's clear band — exactly what
 * interiorForUnit() computes for the committed interior (ADR 0055), fed the
 * draft instead. This is both the preview's interior feed and the warnings
 * source (`solved.overflowUnits`, per-element `pinIgnored`/`overflow`).
 */
export function draftFeedForUnit(draft, unit, rules = {}) {
  if (!unit || !(unit.topUnits > unit.bottomUnits)) return null;
  const band = interiorBand(unit, unit.shelves ?? null);
  const solved = solveInterior(draft.elements, band, interiorSolveOpts(rules, {
    requiredHolesOnly: draft.requiredHolesOnly === true,
    panelBottomUnits: unit.bottomUnits, // the hole ladder's datum (ADR 0061)
  }));
  return { solved, doors: normalizeDoors(draft.doors), band };
}

/**
 * The OPENINGS in the staged composition — the clear spaces a designer can
 * select and split (ADR 0061 item D, the CV gesture the owner described: "CV
 * allows users to select 'an opening' between shelves then split that
 * opening… so a user can select the opening and add X# of shelves").
 *
 * @returns {Array<{elementIndex:number, fromUnits:number, toUnits:number, spanUnits:number}>}
 */
export function draftOpenings(draft, unit, rules = {}) {
  const feed = draftFeedForUnit(draft, unit, rules);
  return feed ? interiorOpenings(feed.solved.elements) : [];
}

/**
 * Adds `count` evenly spaced shelves INTO one opening (ADR 0061 item D).
 *
 * TWO deliberate decisions, both of them the owner's words made literal:
 *
 * 1. **The spacing comes from the shelfGroup machinery, not a second copy of
 *    it.** The opening is temporarily swapped for a `shelfGroup` and SOLVED —
 *    so the positions are the solver's own even pitch, already snapped onto the
 *    32 mm holes (or left free in "required holes only" mode). A group of
 *    `count + 1` is used and its lowest line dropped, which is what makes the
 *    shelves DIVIDE the opening into `count + 1` equal parts instead of
 *    planting a board on the opening's floor.
 * 2. **The result is discrete PINNED shelves, not a group.** The owner needs
 *    them "individually repositionable"; a group is one element with one count.
 *    Materializing the solved lines as pins means each shelf already carries
 *    the exact number a later drag phase edits — the drag becomes an edit to
 *    one `atUnits`, exactly as the door split does (ADR 0060 §11).
 *
 * @returns {boolean} false when the index is not an opening, the count is not
 *   a positive whole number, or the unit cannot be solved.
 */
/**
 * A RUN of discrete shelves filling the whole band — what "Fill with → Shelves"
 * now produces, and what the section row's shelf-count field has to operate on
 * instead of a `shelfGroup`.
 *
 * A run is the whole composition being nothing but open spaces and discrete
 * shelves, alternating. Anything else — a rod, a bank, shoe shelves, a group —
 * means the section is not a plain shelf stack and the row's one-number control
 * has no business rewriting it.
 *
 * @returns {{indices:Array<number>, count:number, locked:number, moved:number}|null}
 */
export function shelfRunFor(draft, unit, rules = {}) {
  const els = Array.isArray(draft?.elements) ? draft.elements : [];
  if (!els.length) return null;
  if (!els.every((el) => el.kind === 'openSpace' || el.kind === 'shelf')) return null;
  const indices = els.map((el, i) => (el.kind === 'shelf' ? i : -1)).filter((i) => i >= 0);
  if (!indices.length) return null;

  const locked = indices.filter((i) => els[i].locked === true).length;
  // MOVED is a FACT, recomputed, not a flag. Every board in a run carries a pin
  // — that is what makes them individually placeable — so a dragged board and a
  // freshly filled one differ only in WHERE they are. The honest question is
  // "are these boards still where an even fill would have put them?", so ask
  // exactly that: collapse the run back to one opening, run the same probe the
  // fill runs, at the CURRENT count, and compare. A `placedByFill` flag would
  // answer a different question and go stale the first time anything else
  // touched the element.
  const reference = evenFillLines(draft, indices.length, unit, rules);
  const actual = indices.map((i) => els[i].atUnits).filter(Number.isFinite);
  let moved = indices.length; // no reference to compare against ⇒ assume deliberate
  if (reference && reference.length === indices.length && actual.length === indices.length) {
    const sorted = [...actual].sort((a, b) => b - a);
    moved = sorted.filter((at, k) => Math.abs(at - reference[k]) > MOVED_EPS).length;
  }
  return { indices, count: indices.length, locked, moved };
}

/**
 * 1/64" — the tolerance for "is this board still where the fill put it".
 *
 * IT IS INSURANCE, NOT CURRENTLY LOAD-BEARING, and that is measured rather than
 * assumed. Setting it to ZERO fails no test: the reference is produced by the
 * same solver over the same band as the boards themselves, so today an
 * untouched board compares EXACTLY equal. It is kept because the reference goes
 * through a different call path than the placement, and a change to either — a
 * different band derivation, a unit conversion — would reintroduce last-decimal
 * drift, at which point the control would start refusing on PRISTINE runs. That
 * is the worst failure available here: it is the common case, and it would look
 * exactly like the bug this work fixes.
 *
 * The UPPER bound IS pinned: raising it past the smallest real move (one 32 mm
 * hole, ≈1.26") fails "a MOVED board stops it too", because a tolerance that
 * swallowed a real move would let the control re-space work done by hand. So
 * the size is bounded from above by a test and from below by an argument —
 * stated that way round so nobody reads the lower half as proven.
 *
 * 1/64" sits far above float noise (~1e-9) and far below anything a designer
 * can express: the length fields round to 1/4", and a real move is eighty times
 * this tolerance.
 */
const MOVED_EPS = 1 / 64;

/**
 * Where an even fill of `count` boards would put them in THIS band — the
 * reference `shelfRunFor` compares against and the positions a re-fill uses.
 * One probe, so "where they would go" and "where they went" cannot disagree.
 */
function evenFillLines(draft, count, unit, rules = {}) {
  if (!Number.isInteger(count) || count <= 0) return null;
  const probe = {
    ...draft,
    elements: [normalizeInteriorElement({ kind: 'shelfGroup', count: count + 1 })],
  };
  const feed = draftFeedForUnit(probe, unit, rules);
  const group = feed?.solved?.elements?.[0];
  const lines = (Array.isArray(group?.shelfLines) ? group.shelfLines : [])
    .filter(Number.isFinite)
    .slice(1)
    .sort((a, b) => b - a);
  return lines.length === count ? lines : null;
}

export function draftInsertShelvesIntoOpening(draft, elementIndex, count, unit, rules = {}, opts = {}) {
  const target = draft.elements[elementIndex];
  if (target?.kind !== 'openSpace') return false;
  if (!Number.isInteger(count) || count <= 0) return false;
  const fixed = opts.fixed === true;
  const doubleThick = opts.doubleThick === true;

  // Solve a PROBE draft in which the opening is the group. Same solver, same
  // band, same hole ladder — the numbers are the app's, not the editor's.
  const probe = {
    ...draft,
    elements: draft.elements.map((el, i) => (i === elementIndex
      ? normalizeInteriorElement({ kind: 'shelfGroup', count: count + 1, fixed, doubleThick })
      : el)),
  };
  const feed = draftFeedForUnit(probe, unit, rules);
  const group = feed?.solved?.elements?.[elementIndex];
  const lines = (Array.isArray(group?.shelfLines) ? group.shelfLines : [])
    .filter(Number.isFinite)
    .slice(1) // drop the line sitting ON the opening's floor
    .sort((a, b) => b - a); // top → bottom, the order the list reads in
  if (lines.length === 0) return false;

  // openSpace / shelf / openSpace / … / openSpace: a space above the top shelf,
  // one between each pair, one below the last — so the composition still reads
  // as openings with shelves in them, which is how the designer selected it.
  const replacement = [normalizeInteriorElement({ kind: 'openSpace' })];
  for (const atUnits of lines) {
    replacement.push(normalizeInteriorElement({ kind: 'shelf', fixed, doubleThick, atUnits }));
    replacement.push(normalizeInteriorElement({ kind: 'openSpace' }));
  }
  draft.elements.splice(elementIndex, 1, ...replacement);
  return true;
}

// ---- SPACES: the primary abstraction (ADR 0065 part 2) ---------------------
// The owner does not compose a list of parts; they think in vertical SPACES and
// what goes in each one ("shelves are generally evenly spaced within a vertical
// space… there may be a vertical space above a drawer bank that is 24" tall to
// serve as a vanity… if we can split openings into different spaces then count
// control within the space is a strong first start"). Two verbs cover it:
// SPLIT a space in two, and FILL a space with a treatment. Both are ordinary
// edits to ordinary elements — see interiorSpaces() for why that is enough.

/**
 * The treatments a space can be filled with, in the order the editor offers
 * them, with the label each one reads as. A LIST, so adding a treatment is a
 * one-line change and the UI has no switch of its own to fall out of step.
 *
 * `counted` says whether the treatment takes a COUNT — the owner's "count
 * control within the space", and the whole point of the feature: N shelves, N
 * drawers, N shoe shelves divide the SPACE's height, never the section's.
 */
export const SPACE_TREATMENTS = Object.freeze([
  { key: 'empty', label: 'Empty', counted: false },
  { key: 'shelves', label: 'Shelves', counted: true, countLabel: 'Shelves' },
  { key: 'drawers', label: 'Drawer bank', counted: true, countLabel: 'Drawers' },
  { key: 'rod', label: 'Rod', counted: false },
  { key: 'shoes', label: 'Shoe shelves', counted: true, countLabel: 'Shelves' },
]);

/**
 * WHERE a space is, as a designer reads a drawing (ADR 0067).
 *
 * The owner's complaint was not that a figure was wrong — it was that a bare
 * height cannot be matched to a region: two spaces of the same height read as
 * the same row twice. A drawing is dimensioned OFF THE FLOOR, so a row states
 * its RANGE off the floor and then its height: `30 1/4"–57" · 27" tall`. Both
 * ends come from the SOLVED element, so the row and the preview cannot differ.
 *
 * @param {{fromUnits:number, toUnits:number, heightUnits:number}} space
 * @param {(units:number)=>string} format the app's ¼"-rounded system formatter
 */
export function spaceRangeLabel(space, format) {
  if (!space || typeof format !== 'function') return '';
  const base = `${format(space.fromUnits)}–${format(space.toUnits)} · ${format(space.heightUnits)} tall`;
  // HANG LENGTH is stated BY NAME on a rod row (owner, 2026-08-11). It is the
  // figure a client asks about — "will my clothes drag?" — and Cabinet Vision
  // does not report it, so this is the one number here that exists nowhere else.
  // For a rod it is also exactly what `heightUnits` already was, which is why
  // the row now says which measurement it is showing instead of leaving the
  // reader to assume it is the opening (it is not; opening = hang + the rod's
  // drop below its board).
  return space.kind === 'rod' && Number.isFinite(space.hangLengthUnits)
    ? `${base} · ${format(space.hangLengthUnits)} hang`
    : base;
}

/** A compact human label for ONE space row ("Space · 3 shelves"). */
export function spaceLabel(space) {
  const t = space?.treatment;
  switch (t?.key) {
    case 'shelves': return `Space · ${t.count} shelves${t.fixed ? ' fixed' : ''}`;
    case 'drawers': return `Space · drawer bank of ${t.count}`;
    case 'shoes': return `Space · ${t.count} shoe shelves (${t.style})`;
    case 'rod': return 'Space · rod';
    default: return 'Space · empty';
  }
}

/**
 * The SPACES of the staged composition, solved in the placed unit — the
 * editor's primary list (ADR 0065 part 2). Same shape as draftOpenings(), which
 * it generalises: an opening is a space whose treatment is `empty`.
 */
export function draftSpaces(draft, unit, rules = {}) {
  const feed = draftFeedForUnit(draft, unit, rules);
  if (!feed) return [];
  // HANG LENGTH needs two things the element list alone cannot say: whether the
  // section has a bottom shelf at all (ADR 0070's per-section flag — switched
  // off, the datum becomes 1" off the FLOOR) and what that clearance is.
  return interiorSpaces(feed.solved.elements, {
    hasBottomShelf: unit?.section?.bottomShelf !== false,
    rodHangFloorClearanceUnits: rules.rodHangFloorClearanceUnits,
  });
}

/**
 * EVERY REGION OF THE BAND IS A SPACE (ADR 0067) — the second half of the
 * healing contract, and the fix for the owner's "I don't understand what the
 * top and bottom are doing".
 *
 * ADR 0065's claim is that an interior READS as a stack of vertical spaces and
 * that "every space IS one element at one index". A pinned board breaks that
 * claim: `[shelf pinned at 60", rod, openSpace]` draws THREE regions (the 23¼"
 * above the board, then the rod's hang zone and the empty space that share the
 * 56" below it) but lists only TWO spaces, because nothing in the element list
 * lives above the board. The designer then matches the two rows to the two
 * regions they can see and reads both figures against the wrong thing.
 *
 * So an unlisted region gains the element it was always missing: an
 * `openSpace`, spliced where the solver says it belongs. Three things make this
 * safe rather than clever:
 *
 * 1. **It changes NO geometry.** The segment held no element, so there was
 *    nothing to share its height with; one flexible element in an otherwise
 *    empty segment simply spans it. Every other segment is untouched — there is
 *    a test asserting the rod and the space below keep their exact spans.
 * 2. **It is driven by the SOLVE, not by a guess at the list.** Only the solver
 *    knows which pins actually held; a pin that could not hold (`pinIgnored`)
 *    cuts no segment, so no phantom space appears above a broken pin.
 * 3. **It is idempotent** — the segment now holds an element, so nothing is
 *    inserted on the next render — and it is STAGED like every other heal:
 *    Cancel leaves the stored section byte-for-byte as it was.
 *
 * Recorded on `draft.healed` under the `bandSpace` role so the inline note says
 * a space appeared, exactly as it says a board appeared.
 *
 * @returns {boolean} true when the draft changed (the caller re-renders).
 */
export function draftEnsureBandSpaces(draft, unit, rules = {}) {
  const feed = draftFeedForUnit(draft, unit, rules);
  const gaps = feed?.solved?.emptySegments ?? [];
  if (!gaps.length) return false;
  // Descending, so an earlier splice cannot shift a later insertion point.
  const ordered = gaps.slice().sort((a, b) => b.insertIndex - a.insertIndex);
  for (const gap of ordered) {
    draft.elements.splice(gap.insertIndex, 0, normalizeInteriorElement({ kind: 'openSpace' }));
  }
  draft.healed = [...(Array.isArray(draft.healed) ? draft.healed : []),
    ...ordered.map((gap) => ({ index: gap.insertIndex, role: 'bandSpace' }))];
  return true;
}

/** The solved space at `elementIndex`, or null when that element is not one. */
function solvedSpaceAt(draft, elementIndex, unit, rules) {
  return draftSpaces(draft, unit, rules).find((s) => s.elementIndex === elementIndex) ?? null;
}

/**
 * SPLITS one space into TWO at an elevation off the floor (ADR 0065 part 2).
 *
 * What lands in the list is `[the space, a FIXED shelf pinned at atUnits, a new
 * empty space]`. Three decisions, recorded because each had a live rival:
 *
 * 1. **The divider is a FIXED shelf**, which is what the owner's own example
 *    describes — "an 84" section may have two ~40" tall openings with a FIXED
 *    shelf in the middle". An adjustable board is not a division; it is a shelf
 *    the client can move, and the two "spaces" would not survive it.
 * 2. **The upper part KEEPS the space's treatment; the lower starts empty.**
 *    The list reads top → bottom and a pin's semantics are already "everything
 *    before me solves above me", so this is what the existing solver does with
 *    no special case at all. A treatment with a COUNT then divides a shorter
 *    space, which is not a bug to hide — it is the model saying out loud that
 *    the count divides the SPACE, and the count control is right there.
 * 3. **The height is the designer's, defaulting to the midpoint** — the door
 *    split's convention (ADR 0060 item 3), entered on the numeric pad.
 *
 * The pin is subject to everything a pin is subject to: in full-drilling mode
 * it snaps to the nearest system hole (ADR 0061), so the two spaces sum to the
 * original LESS the board's thickness but the division itself may move by up to
 * half a pitch. In "required holes only" mode the typed figure is law.
 *
 * @param {number|null} atUnits the elevation off the floor of the new board's
 *   TOP surface; null takes the space's midpoint.
 * @returns {boolean} false when the index is not a space, the height is junk or
 *   would leave one side with no room, or the unit cannot be solved.
 */
export function draftSplitSpace(draft, elementIndex, atUnits, unit, rules = {}) {
  const el = draft.elements[elementIndex];
  if (!el || !isSpaceKind(el.kind)) return false;
  const space = solvedSpaceAt(draft, elementIndex, unit, rules);
  if (!space) return false;
  const at = atUnits === null || atUnits === undefined ? spaceSplitDefaultUnits(space) : atUnits;
  if (!Number.isFinite(at) || !(at > 0)) return false;
  const divider = normalizeInteriorElement({ kind: 'shelf', fixed: true, atUnits: at });
  const t = shelfThicknessUnits(divider, rules.sectionShelfThicknessUnits ?? 0.75,
    rules.doubleThickShelfMultiplier ?? 2);
  // Both sides must be real space. A "split" that leaves nothing on one side is
  // not a split; it is a board planted on the space's own edge, and the solver
  // would report it as an overflow the designer never asked for.
  if (!(at - t > space.fromUnits + EPS) || !(at < space.toUnits - EPS)) return false;
  draft.elements.splice(elementIndex, 1, el, divider, normalizeInteriorElement({ kind: 'openSpace' }));
  draft.elements = ensureInteriorStructure(draft.elements);
  return true;
}

/**
 * SPLITS one space into `count` EQUAL openings (owner, 2026-08-11):
 *
 * > "the designer should have a space and indicating the number of spaces the
 * > opening will be evenly split into (e.g, 2, 3, 4, etc.) Otherwise there is
 * > no easy way to take a vertical space and automate the adding of shelves"
 *
 * ⚠ **This is not "Fill with shelves".** A shelfGroup is ONE element that draws
 * N shelves inside itself — it stays a single row, so the openings it creates
 * can never be seen, split again, or filled with anything. This inserts REAL
 * fixed boards, so the space becomes `count` sibling spaces, each a full row
 * with its own Split and Fill. That is what makes it compose: split into 3,
 * then put a rod in one and drawers in another.
 *
 * The arithmetic is stated in OPENINGS because that is what the designer asked
 * for and what a drawing is dimensioned in — `count` openings separated by
 * `count - 1` boards, so each opening is
 * `(span - (count - 1) x thickness) / count`. The boards are FIXED, for the
 * same reason draftSplitSpace's single divider is: an adjustable board is not a
 * division, it is a shelf the client can move.
 *
 * The TOPMOST opening keeps the original space's treatment and the rest start
 * empty — the same convention as the single split, so a rod stays put and the
 * new openings appear beneath it.
 *
 * ⚠ In full 32mm-rows mode each board is a PIN and therefore snaps to the
 * nearest hole (ADR 0061), so the openings can end up differing by up to one
 * pitch. "Required holes only" honours the exact even division. That is
 * existing, documented pin behaviour, not a bug in this helper.
 *
 * @returns {boolean} false when the index is not a space, the count is under 2,
 *   or the span cannot hold that many openings.
 */
export function draftSplitSpaceEvenly(draft, elementIndex, count, unit, rules = {}) {
  const el = draft.elements[elementIndex];
  if (!el || !isSpaceKind(el.kind)) return false;
  const n = Number.isInteger(count) ? count : 0;
  if (n < 2) return false;
  const feed = draftFeedForUnit(draft, unit, rules);
  const space = solvedSpaceAt(draft, elementIndex, unit, rules);
  if (!feed || !space) return false;
  const probe = normalizeInteriorElement({ kind: 'shelf', fixed: true });
  const t = shelfThicknessUnits(probe, rules.sectionShelfThicknessUnits ?? 0.75,
    rules.doubleThickShelfMultiplier ?? 2);
  const span = space.toUnits - space.fromUnits;
  const opening = (span - (n - 1) * t) / n;
  // Every opening must be REAL space. Asked for more divisions than the span
  // can hold, the honest answer is to refuse and say so, not to plant boards
  // on top of each other.
  if (!(opening > EPS)) return false;

  // ⚠ FREEZE THE SPACE'S OWN EDGES FIRST.
  // The new boards are PINS, but the space's boundaries may be ordinary in-flow
  // boards, and a pin taking space above them makes everything below re-share
  // what is left. Live testing caught exactly that: splitting a 37 1/2" rod
  // space three ways produced 12 1/4" / 11 3/4" / 24 1/2", because the board
  // under the space drifted down and the lowest new opening merged into the
  // segment beneath it. Pinning the adjacent boards where they ALREADY are
  // makes "this opening divides, nothing else moves" actually true.
  const solvedEls = feed.solved.elements;
  for (const i of [elementIndex - 1, elementIndex + 1]) {
    const board = draft.elements[i];
    const solvedBoard = solvedEls[i];
    if (!board || board.kind !== 'shelf' || Number.isFinite(board.atUnits)) continue;
    if (!Number.isFinite(solvedBoard?.toUnits)) continue;
    draft.elements[i] = normalizeInteriorElement({ ...board, atUnits: solvedBoard.toUnits });
  }
  const out = [el];
  for (let k = 1; k <= n - 1; k++) {
    // `atUnits` is the board's TOP surface, so board k's top is the bottom of
    // the k-th opening counted from the top of the space.
    out.push(
      normalizeInteriorElement({ kind: 'shelf', fixed: true, atUnits: space.toUnits - k * opening - (k - 1) * t }),
      normalizeInteriorElement({ kind: 'openSpace' }),
    );
  }
  draft.elements.splice(elementIndex, 1, ...out);
  draft.elements = ensureInteriorStructure(draft.elements);
  return true;
}

/**
 * The height each opening gets if a space is split `count` ways — the figure
 * the editor shows BEFORE the designer commits, so "split into 4" is not a
 * guess. Null when the span cannot hold that many.
 */
export function evenSplitOpeningUnits(space, count, rules = {}) {
  const n = Number.isInteger(count) ? count : 0;
  if (!space || n < 2) return null;
  const t = rules.sectionShelfThicknessUnits > 0 ? rules.sectionShelfThicknessUnits : 0.75;
  const opening = ((space.toUnits - space.fromUnits) - (n - 1) * t) / n;
  return opening > EPS ? opening : null;
}

/**
 * The face height a bank of `count` drawers should be seeded at when a space is
 * FILLED with drawers — the TALLEST height on the shop's standard list that
 * still fits (ADR 0065 part 2).
 *
 * Every candidate comes from `drawer_face_heights`, the rule the per-drawer
 * select already offers, so the seed is never a computed height a designer
 * cannot pick back. Nothing fits → the SHORTEST standard height, which lets the
 * solver report the honest overflow instead of the editor refusing the edit.
 */
function fitDrawerFaceUnits(availableUnits, count, rules = {}) {
  const list = (Array.isArray(rules.drawerFaceHeights) ? rules.drawerFaceHeights : [])
    .filter((h) => Number.isFinite(h) && h > 0)
    .sort((a, b) => b - a);
  if (!list.length) return rules.defaultDrawerHeightUnits > 0 ? rules.defaultDrawerHeightUnits : 6.25;
  const opts = interiorSolveOpts(rules);
  for (const h of list) {
    const faces = Array.from({ length: count }, () => ({ heightUnits: h }));
    if (drawerBankBandHeightUnits(faces, opts) <= availableUnits + 1e-9) return h;
  }
  return list[list.length - 1];
}

/** The element a treatment becomes. `null` for a treatment we do not know. */
function treatmentElement(key, { count, fixed, style, faceUnits }) {
  switch (key) {
    case 'empty': return normalizeInteriorElement({ kind: 'openSpace' });
    case 'shelves': return normalizeInteriorElement({ kind: 'shelfGroup', count, fixed: fixed === true });
    case 'shoes': return normalizeInteriorElement({ kind: 'shoeShelves', style, count });
    case 'rod': return normalizeInteriorElement({ kind: 'rod', hangCategory: null });
    case 'drawers': return normalizeInteriorElement({
      kind: 'drawerBank', drawers: Array.from({ length: count }, () => ({ heightUnits: faceUnits })),
    });
    default: return null;
  }
}

/**
 * FILLS one space with a treatment (ADR 0065 part 2) — the second of the two
 * space verbs, and the owner's "count control within the space".
 *
 * The treatment REPLACES the space's element AT ITS INDEX, which is what keeps
 * the space concept intact: the space does not become "some shelves that happen
 * to be here", it stays one space whose height is what the count divides.
 *
 * SHELVES ARE THE EXCEPTION, and were the bug (owner, 2026-08-30). This used to
 * say "N shelves become ONE `shelfGroup` ... rather than N loose boards spread
 * across whatever the section left over", and that was the wrong trade: a group
 * has no per-board identity, so the boards could not be moved, pinned, dragged
 * or locked, and its first line landed on the space's own floor. They are now
 * discrete PINNED shelves via draftInsertShelvesIntoOpening — evenly placed to
 * begin with, because that is a sensible starting point, but each one a real
 * element the designer can then move. "Evenly spaced" is a default, not a
 * cage.
 *
 * Every invariant still applies afterwards: `ensureInteriorStructure` seats a
 * new bank between its two fixed boards (ADR 0062/0063) and gives a new rod its
 * support shelf (ADR 0060 item 4), and the group's lines still snap to the
 * 32 mm ladder (ADR 0061) when the section is drilled in full.
 *
 * @param {{key:string, count?:number, fixed?:boolean, style?:string}} treatment
 * @returns {boolean} false for a non-space index, an unknown treatment, or a
 *   count that is not a positive whole number where one is needed.
 */
/**
 * The neighbouring spaces this one could be MERGED into, and why not (RS-8).
 *
 * The owner: *"need to be able to remove spaces once they are created or have
 * to build from scratch each time."* The Space row offered Split, Split evenly
 * and Fill and no inverse at all. A split could be undone through the advanced
 * element list in two steps — delete the new space, then delete the divider —
 * which is recoverable but undiscoverable, and the two steps are order
 * sensitive in a way nothing says out loud (see `draftMergeSpace`).
 *
 * A merge is the exact inverse of `draftSplitSpace`: take out the divider
 * between two spaces and let one span the whole. Two things decide whether
 * that is legal, and both are measured rather than assumed:
 *
 * 1. **The two spaces must be separated by exactly ONE element.** More than one
 *    board between them is not a division this can undo.
 * 2. **The divider must not be STRUCTURAL.** Some boards are put straight back
 *    by `ensureInteriorStructure` — a drawer bank's sandwich, and the support
 *    shelf above a rod — because the shop cannot build the composition without
 *    them (ADR 0060 item 4, ADR 0062 item 6). Rather than enumerate which
 *    arrangements those are, this ASKS: perform the removal on a copy, re-run
 *    the invariants, and see whether the board comes back. The answer is then
 *    the invariant's own, and cannot drift from it as the invariants change.
 *
 * ⚠ **THAT CHECK DOES NOT FIRE TODAY, and this says so rather than implying
 * otherwise.** Removing a divider on its own IS undone for some arrangements —
 * `openSpace | shelf | rod` restores the rod's support shelf, and a drawer
 * bank's sandwich comes straight back. But a MERGE also removes the absorbed
 * space, so it either takes away the very rod or bank that required the board,
 * or leaves that element's other side untouched. Every arrangement I could
 * construct — plain dividers, a rod above and below a board, a bank's
 * sandwich, two stacked banks sharing a board — is permitted.
 *
 * It is kept anyway, for two reasons that are not "just in case": it is derived
 * from the invariant itself rather than from a list of kinds, so it stays
 * correct if `ensureInteriorStructure` gains a case; and it fails CLOSED,
 * refusing rather than performing a merge the invariants would silently undo
 * on the next render. A test pins the property it actually protects — that a
 * merge never leaves work for the invariants — rather than pretending to
 * exercise the refusal.
 *
 * @returns {{above: MergeTarget|null, below: MergeTarget|null}} where a
 *   MergeTarget is `{ dividerIndex, otherIndex, otherTreatmentKey, blocked }`
 *   and `blocked` is null when the merge would succeed, or 'structural'.
 */
export function mergeSpaceTargets(draft, elementIndex, unit, rules = {}, opts = {}) {
  // `ensureStructure` is the invariant checker this guard consults, injectable
  // for ONE reason: no real composition can reach the refusal (see above), so
  // without a seam the refusal path would never execute — not in a test, not
  // anywhere — until the day an invariant change made it reachable, in front
  // of a designer. "Fails closed" is a property of code that RUNS, so the
  // tests inject a checker that reports a board coming back and assert the
  // refusal actually refuses. Production always gets the real one.
  const ensureStructure = typeof opts.ensureStructure === 'function' ? opts.ensureStructure : ensureInteriorStructure;
  const spaces = draftSpaces(draft, unit, rules);
  const at = spaces.findIndex((s) => s.elementIndex === elementIndex);
  const out = { above: null, below: null };
  if (at < 0) return out;
  // The list reads TOP to BOTTOM, so the previous entry is the space above.
  for (const [dir, neighbour] of [['above', spaces[at - 1]], ['below', spaces[at + 1]]]) {
    if (!neighbour) continue;
    const lo = Math.min(elementIndex, neighbour.elementIndex);
    const hi = Math.max(elementIndex, neighbour.elementIndex);
    if (hi - lo !== 2) continue; // not exactly one element between them
    const dividerIndex = lo + 1;
    const divider = draft.elements[dividerIndex];
    if (!divider || isSpaceKind(divider.kind)) continue;
    const trial = draft.elements.filter((_, i) => i !== dividerIndex && i !== neighbour.elementIndex);
    const healed = ensureStructure(trial);
    out[dir] = {
      dividerIndex,
      otherIndex: neighbour.elementIndex,
      otherTreatmentKey: neighbour.treatment?.key ?? 'empty',
      blocked: healed.length > trial.length ? 'structural' : null,
    };
  }
  return out;
}

/**
 * MERGES this space with the one above or below it, undoing a split (RS-8).
 *
 * **The space the designer is standing on survives; the neighbour is absorbed.**
 * That is the only rule, and it is deliberately not conditional on which side
 * happens to hold something — a rule that picked "whichever is not empty" would
 * be a guess dressed as a convenience, and the designer could not predict it
 * from the button they are about to press. The caller states the consequence
 * instead: the control names the treatment that is about to be discarded.
 *
 * Merging DOWN from a freshly split space is therefore the exact inverse of
 * `draftSplitSpace`, which keeps the upper space's treatment and starts the
 * lower one empty.
 *
 * **The divider and the absorbed space go in ONE splice**, and that matters.
 * Removing only the absorbed space leaves the divider still cutting the band,
 * so `draftEnsureBandSpaces` sees an unlisted region and inserts a fresh
 * `openSpace` into it on the very next render — the merge appears to do
 * nothing. Measured; it is the trap the two-step manual route walks into when
 * the steps are taken in the wrong order.
 *
 * @param {'above'|'below'} dir which neighbour to absorb.
 * @returns {boolean} false when there is no such neighbour, or the divider
 *   between them is structural and would be restored.
 */
export function draftMergeSpace(draft, elementIndex, dir, unit, rules = {}, opts = {}) {
  const target = mergeSpaceTargets(draft, elementIndex, unit, rules, opts)[dir];
  if (!target || target.blocked) return false;
  const first = Math.min(target.dividerIndex, target.otherIndex);
  draft.elements.splice(first, 2);
  draft.elements = restructure(draft.elements);
  return true;
}

/**
 * What the Merge control SAYS and whether it is pressable, for one direction.
 *
 * Lives beside the rule rather than in the view for two reasons. The wording
 * is part of the decision — "the space you are on survives" is only a fair rule
 * if the button names what it is about to discard — and putting it here means
 * the REFUSAL's presentation can be tested at all. The disabled branch is
 * otherwise unreachable for the same reason the refusal is (see
 * `mergeSpaceTargets`), and an unreachable branch that has never run once is
 * not a safeguard, it is an assumption.
 *
 * @param {{otherTreatmentKey:string, blocked:string|null}|null} target
 * @param {'above'|'below'} dir
 * @param {Array<{key:string,label:string}>} [treatments] SPACE_TREATMENTS, for the label
 * @returns {{label:string, disabled:boolean, title:string}|null}
 */
export function mergeControlFor(target, dir, treatments = SPACE_TREATMENTS) {
  if (!target) return null;
  const label = dir === 'above' ? 'Space above' : 'Space below';
  if (target.blocked === 'structural') {
    return {
      label,
      disabled: true,
      // Never merely greyed out: the owner has twice reported controls that
      // sit there disabled without saying why.
      title: 'The board between them is structural — a drawer bank’s shelf or a rod’s support — so it cannot be removed.',
    };
  }
  const losing = treatments.find((t) => t.key === target.otherTreatmentKey);
  return {
    label,
    disabled: false,
    title: target.otherTreatmentKey === 'empty'
      ? `Remove the board between them; this space grows into the empty one ${dir}.`
      : `Remove the board between them; the ${losing?.label ?? target.otherTreatmentKey} in the space ${dir} is discarded.`,
  };
}

export function draftFillSpace(draft, elementIndex, treatment, unit, rules = {}) {
  const el = draft.elements[elementIndex];
  if (!el || !isSpaceKind(el.kind)) return false;
  const spec = SPACE_TREATMENTS.find((t) => t.key === treatment?.key);
  if (!spec) return false;
  const count = Number.isInteger(treatment.count) ? treatment.count : 0;
  if (spec.counted && !(count > 0)) return false;
  const style = SHOE_SHELF_STYLES.includes(treatment.style) ? treatment.style : 'FSS';

  // A DRAWER bank is the one treatment whose height is not the space's: the
  // faces are standard heights, so the seed has to be chosen against the room
  // that will actually be left once the bank's own two boards are in place.
  let faceUnits = rules.defaultDrawerHeightUnits > 0 ? rules.defaultDrawerHeightUnits : 6.25;
  if (spec.key === 'drawers') {
    const space = solvedSpaceAt(draft, elementIndex, unit, rules);
    if (!space) return false;
    const probe = draft.elements.map((e, i) => (i === elementIndex
      ? normalizeInteriorElement({ kind: 'drawerBank', drawers: [{ heightUnits: faceUnits }] }) : e));
    const boardsAdded = ensureInteriorStructure(probe).length - probe.length;
    const shelfT = rules.sectionShelfThicknessUnits > 0 ? rules.sectionShelfThicknessUnits : 0.75;
    faceUnits = fitDrawerFaceUnits(space.heightUnits - boardsAdded * shelfT, count, rules);
  }

  // SHELVES ARE REAL BOARDS, not a count (owner, 2026-08-30). This used to make
  // ONE shelfGroup, and both of the owner's complaints came straight out of it:
  //
  //   - "it is adding a bottom shelf that cannot be removed or moved" — a group
  //     lays its lines at `from + pitch*k` from k=0, so the FIRST one lands on
  //     the space's own floor. Filling a freshly split space put a board right
  //     on top of the divider that made it. It could not be removed because a
  //     group has no per-board identity: the only edit is the count, and
  //     changing that moves every other board too.
  //   - "I seem to have lost the ability to move shelves and adjust the spacing
  //     within a vertical space" — the same missing identity. Nothing can
  //     select, pin, drag or lock a board that is not an element.
  //
  // draftInsertShelvesIntoOpening already does the right thing and has said so
  // all along: "The result is discrete PINNED shelves, not a group. The owner
  // needs them 'individually repositionable'." Its own note even warns "this is
  // not Fill with shelves". Two documented decisions contradicted each other
  // and this is the one that was wrong; they now share an implementation, so
  // they cannot drift apart again.
  //
  // The space becomes an opening first, so the insert has the opening it
  // expects, and is put back untouched if the insert refuses — a refused fill
  // must not leave the composition worse than it found it.
  if (spec.key === 'shelves') {
    const before = draft.elements[elementIndex];
    draft.elements.splice(elementIndex, 1, normalizeInteriorElement({ kind: 'openSpace' }));
    const inserted = draftInsertShelvesIntoOpening(draft, elementIndex, count, unit, rules, {
      fixed: treatment.fixed === true,
      doubleThick: treatment.doubleThick === true,
    });
    if (!inserted) {
      draft.elements.splice(elementIndex, 1, before);
      return false;
    }
    draft.elements = restructure(draft.elements);
    return true;
  }

  const filled = treatmentElement(spec.key, { count, fixed: treatment.fixed, style, faceUnits });
  if (!filled) return false;
  draft.elements.splice(elementIndex, 1, filled);
  draft.elements = restructure(draft.elements);
  return true;
}

/**
 * Commits the draft to the model — the editor's Apply. One call site, two
 * writes: setSectionInterior() (which re-mirrors shelfCount/drawerCount and
 * marks the section custom by storing the composition — ADR 0054) and
 * setSectionDoors(). When `doorsBlocked` (the depth rule) the doors write is
 * REFUSED — the committed doors stay whatever they were — while the interior
 * commit still lands: depth gates doors, not interiors.
 *
 * @returns {{ok: boolean, doorsApplied: boolean}}
 */
export function applyInteriorDraft(model, sectionId, draft, { doorsBlocked = false } = {}) {
  // The invariants are re-established at the COMMIT boundary too (ADR 0062):
  // reordering does not heal (see draftMove), so this is what guarantees the
  // model never stores a bank or a rod without its structural shelves.
  draft.elements = ensureInteriorStructure(draft.elements);
  if (!model.setSectionInterior(sectionId, draft.elements)) return { ok: false, doorsApplied: false };
  // The drilling mode commits with the composition — it IS part of it (ADR
  // 0061 item B): the same shelves mean different holes in the two modes.
  model.setSectionRequiredHolesOnly?.(sectionId, draft.requiredHolesOnly === true);
  if (doorsBlocked && draft.doors) return { ok: true, doorsApplied: false };
  model.setSectionDoors(sectionId, draft.doors ?? null);
  return { ok: true, doorsApplied: true };
}

/**
 * Everything the live mini-preview needs to render ONLY this section's stack
 * with the REAL elevation renderer:
 *
 *   walls — one synthetic measured wall exactly as wide as the section's
 *     opening plus its flanking panels (the panels are found by GEOMETRY —
 *     the panel ending at the opening's start / starting at its end — not by
 *     index, because a zero-width panel is skipped by the layout and would
 *     shear an index-based pairing);
 *   opts — the app's own elevationRenderOpts() with every feed substituted:
 *     layout/sections/stacks reduced to this one run slot (shifted so the
 *     unit starts at x 0), the space feeds (obstructions, labels, reference
 *     lines, grid, L-shelves, abutting panels) blanked, and `interiorFor`
 *     answering the EDITED unit from the staged draft while every other unit
 *     in the stack keeps its committed feed;
 *   editedUnit — the placed unit being edited (resolved depth for the door
 *     rule, band for warnings).
 *
 * Returns null when the section is not placeable (no layout on its wall) —
 * the editor then shows no preview rather than guessing at one.
 *
 * @param {object} model the sketch model
 * @param {object} rules resolved business rules
 * @param {string} sectionId the section (run or stacked) being edited
 * @param {{elements: Array<object>, doors: object|null}} draft the staged copy
 * @param {object} baseOpts the app's elevationRenderOpts() (or a test double)
 * @returns {{walls: Array<object>, opts: object, editedUnit: object}|null}
 */
export function previewElevation(model, rules, sectionId, draft, baseOpts) {
  const all = model.state.sections;
  const section = all.find((s) => s.id === sectionId);
  if (!section) return null;
  // A stacked unit previews inside its whole stack: walk down to the RUN base.
  let run = section;
  const seen = new Set();
  while (run.stackOnId && !seen.has(run.id)) {
    seen.add(run.id);
    const below = all.find((s) => s.id === run.stackOnId);
    if (!below) break;
    run = below;
  }
  const wallId = run.wallId;
  const layout = model.sectionLayoutForWall(wallId);
  const runSections = model.sectionsForWall(wallId);
  const i = runSections.findIndex((s) => s.id === run.id);
  const item = layout?.items?.[i];
  if (!item) return null;

  // Flanking panels by geometry (see the doc comment above).
  const panels = Array.isArray(layout.panels) ? layout.panels : [];
  const openEnd = item.startUnits + item.openingUnits;
  const left = panels.find((p) => Math.abs(p.startUnits + p.widthUnits - item.startUnits) < EPS) ?? null;
  const right = panels.find((p) => Math.abs(p.startUnits - openEnd) < EPS) ?? null;
  const shift = left ? left.startUnits : item.startUnits;
  const spanUnits = Math.max((right ? right.startUnits + right.widthUnits : openEnd) - shift, 1);

  const stacks = model.stacksForWall(wallId);
  const stack = stacks[i] ?? { units: [] };
  const editedUnit = stack.units.find((u) => u.section?.id === sectionId) ?? stack.units[0] ?? null;
  if (!editedUnit) return null;

  const previewLayout = {
    ...layout,
    runUnits: spanUnits,
    overflowUnits: 0, // a run-level overflow is the WALL's warning, not this unit's
    startClearanceUnits: 0,
    endClearanceUnits: 0,
    items: [{ ...item, startUnits: item.startUnits - shift }],
    panels: [left, right].filter(Boolean).map((p) => ({ ...p, startUnits: p.startUnits - shift })),
  };
  const resolvedSections = baseOpts.sectionsFor?.(wallId) ?? runSections;

  const walls = [{
    id: wallId,
    segmentKind: 'wall',
    wallNumber: null,
    isMeasured: true,
    lengthUnits: spanUnits,
    includeInElevation: true,
    start: { x: 0, y: 0 },
    end: { x: 1, y: 0 },
  }];

  const opts = {
    ...baseOpts,
    // The preview is the SECTION, not the room: space furniture goes.
    referenceLines: [],
    gridVisible: false,
    selectedId: null,
    annotations: [],
    wallLabels: [],
    wallHeightProfileFor: () => null, // flat at the ceiling; the band already carries soffit/target effects
    obstructionsFor: () => [],
    obstructionCodeFor: () => '',
    lShelfLegsFor: () => [],
    abuttingPanelsFor: () => [],
    connectingShelvesFor: () => [],
    wallLabelsFor: () => [],
    sectionLayoutFor: () => previewLayout,
    // The row↔picture highlight targets (ADR 0067). Asked for HERE, in the one
    // place that is the editor's own preview, so no wall elevation carries them.
    showSpaceRegions: true,
    // Shelf DRAG targets (owner ruling 5, 2026-08-29 — the section editor
    // first; the wall elevation is a separate decision, because its commit path
    // re-runs structural healing with nowhere to show what it healed).
    //
    // Per UNIT, not per preview: this drawing carries the whole stack, but only
    // the EDITED unit answers from the draft (see `interiorFor` just below, and
    // note the identical test). An element index only means something inside
    // that draft, so a stack-mate must not offer targets that would write to
    // the wrong composition.
    shelfDragTargetsFor: (u) => u.section?.id === sectionId,
    // Fit the viewBox to the drawing (owner, 2026-08-30). This preview is one
    // tall narrow unit in a near-square panel, so the renderer's wall-shaped
    // 1200x560 box costs ~60% of the available height. The wall elevation is
    // wall-shaped and does not want this; the measurement sheet is a fixed
    // printed page and must not have it.
    fitViewBoxToContent: true,
    sectionsFor: () => [resolvedSections[i]],
    stacksFor: () => [stack],
    // The staged draft rides in HERE: the edited unit answers from the draft,
    // its stack-mates keep their committed compositions. Same solver, same
    // band, same marks module as the wall elevation — the preview IS the
    // renderer (ADR 0056).
    interiorFor: (u) => (u.section?.id === sectionId
      ? draftFeedForUnit(draft, u, rules)
      : baseOpts.interiorFor?.(u) ?? null),
  };

  return { walls, opts, editedUnit };
}
